import {
  Button,
  Disclaimer,
  Div,
  H5,
  ModalPortal,
  P,
  SaasEligibleLocation,
  TooltipIcon,
  displayCentsInDollars,
  lightTheme,
} from '@vgrubs/components';
import {
  SAAS_ELIGIBLE_SERVICES,
  SAAS_ELIGIBLE_TOTAL_THRESHOLD,
  openLocationInNewTab,
  qualifyingReasons,
  qualifyingServices,
} from './saasEligibility';

type Props = {
  /** Null keeps it closed. */
  item: SaasEligibleLocation | null;
  onClose: () => void;
};

/**
 * Why one location is on the SaaS eligible list: what each service brought it
 * over the last 30 days, the amounts that reached their own threshold in
 * green, and under them the reasons in words ("Qualifies because of
 * Recovered orders"), with a note when it is listed on the three together.
 */
export const SaasEligibleDetailsModal = ({ item, onClose }: Props) => (
  <ModalPortal
    isOpen={item != null}
    closeModal={onClose}
    backgroundColor={lightTheme.white}
    padding="24px"
    logo={false}
    width="480px"
    modalOverflow="auto"
    titleWithExit={item?.locationName ?? ''}
    zIndex={135}
  >
    {item && (
      <>
        <Disclaimer $m="0" $mb="16" $left $color={lightTheme.lightGray}>
          {[item.ownerName, `Last ${item.windowDays} days`]
            .filter(Boolean)
            .join(' · ')}
        </Disclaimer>

        {SAAS_ELIGIBLE_SERVICES.map(({ key, label, threshold, tooltip }) => {
          const reached = item[key] >= threshold;
          return (
            <Div
              key={key}
              $flex
              $alignCenter
              $between
              $gap="12px"
              $borderBottom
              $borderColor={lightTheme.lightGray}
              $p="10px 0"
            >
              <Div>
                <Div $flex $alignCenter $gap="8px">
                  <P $m="0">{label}</P>
                  <TooltipIcon tooltip={tooltip} />
                </Div>
                <Disclaimer $m="0" $left $color={lightTheme.lightGray}>
                  Qualifies from {displayCentsInDollars(threshold)}
                </Disclaimer>
              </Div>
              <P
                $m="0"
                $bold={reached}
                $color={reached ? lightTheme.green : lightTheme.darkGray}
              >
                {displayCentsInDollars(item[key])}
              </P>
            </Div>
          );
        })}

        {/* Why it is listed, one line per service that reached its own
            threshold, in place of a total that explained nothing. */}
        <Div $flex $alignStart $between $gap="16px" $mt="16">
          <H5 $m="0">Qualifies because of</H5>
          <Div $flex $column $alignEnd $gap="4px">
            {qualifyingReasons(item).map((reason) => (
              <H5 key={reason} $m="0" $right $color={lightTheme.green}>
                {reason}
              </H5>
            ))}
          </Div>
        </Div>

        {qualifyingServices(item).length === 0 && (
          <Disclaimer $mt="8" $mb="0" $left $color={lightTheme.lightGray}>
            No service reached its own threshold, but together they come to{' '}
            {displayCentsInDollars(item.total)}, over{' '}
            {displayCentsInDollars(SAAS_ELIGIBLE_TOTAL_THRESHOLD)}.
          </Disclaimer>
        )}

        <Button
          $selected
          $w="100%"
          $wSM="100%"
          $mt="24"
          onClick={() => openLocationInNewTab(item.locationId)}
        >
          Open Location
        </Button>
      </>
    )}
  </ModalPortal>
);
